import React from 'react';
import { X } from 'lucide-react';

interface ImageZoomModalProps {
  isOpen: boolean;
  imageUrl: string | null;
  altText: string;
  onClose: () => void;
}

const ImageZoomModal: React.FC<ImageZoomModalProps> = ({
  isOpen,
  imageUrl,
  altText,
  onClose,
}) => {
  const [zoom, setZoom] = React.useState(100);

  // ESC key to close modal
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [isOpen, onClose]);

  // Mouse wheel zoom
  React.useEffect(() => {
    const handleWheel = (e: WheelEvent) => {
      if (!isOpen) return;
      e.preventDefault();
      setZoom(prev => {
        const newZoom = e.deltaY > 0 
          ? Math.max(prev - 10, 50) 
          : Math.min(prev + 10, 200);
        return newZoom;
      });
    };

    if (isOpen) {
      window.addEventListener('wheel', handleWheel, { passive: false });
      return () => window.removeEventListener('wheel', handleWheel);
    }
  }, [isOpen]);

  if (!isOpen || !imageUrl) return null;

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-80 p-4"
      onClick={handleBackdropClick}
    >
      {/* Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 p-2 bg-white rounded-full shadow-lg hover:bg-gray-100 transition"
        title="Close (Esc)"
      >
        <X size={24} className="text-gray-800" />
      </button>

      {/* Image Container - Minimalist */}
      <div className="flex items-center justify-center max-w-5xl max-h-[90vh] w-full">
        <img
          src={imageUrl}
          alt={altText}
          style={{
            width: `${zoom}%`,
            height: 'auto',
            transition: 'width 0.2s ease',
          }}
          className="rounded-lg shadow-2xl"
        />
      </div>
    </div>
  );
};

export default ImageZoomModal;
